import { api } from "@/lib/api-client";
import type {
  PaginatedResponse,
  PortfolioImage,
  PortfolioItem,
  PortfolioItemFilters,
  PortfolioItemInput,
  PublicPortfolioItemFilters,
} from "./types";

function listPath(filters: PortfolioItemFilters) {
  const params = new URLSearchParams({ page: "1", pageSize: "100", includeDeleted: "false" });
  if (filters.search) params.set("search", filters.search);
  if (filters.status && filters.status !== "ALL") params.set("status", filters.status);
  if (filters.categoryId) params.set("categoryId", filters.categoryId);
  return `/portfolio/manage?${params.toString()}`;
}

function publicListPath(filters: PublicPortfolioItemFilters) {
  const params = new URLSearchParams({ page: "1", pageSize: "100" });
  if (filters.search) params.set("search", filters.search);
  if (filters.category && filters.category !== "all") params.set("category", filters.category);
  return `/portfolio?${params.toString()}`;
}

function normalizeInput(input: PortfolioItemInput): PortfolioItemInput {
  return {
    ...input,
    slug: input.slug || undefined,
    description: input.description || null,
    clientName: input.clientName || null,
    year: input.year || null,
    categoryId: input.categoryId || null,
    sortOrder: input.sortOrder ?? 0,
    featured: input.featured ?? false,
    status: input.status ?? "DRAFT",
  };
}

export const portfolioItemsApi = {
  list: (filters: PortfolioItemFilters) => api.get<PaginatedResponse<PortfolioItem>>(listPath(filters)),
  detail: (id: string) => api.get<PortfolioItem>(`/portfolio/manage/${id}`),
  publicList: (filters: PublicPortfolioItemFilters) =>
    api.get<PaginatedResponse<PortfolioItem>>(publicListPath(filters)),
  publicDetail: (slug: string) => api.get<PortfolioItem>(`/portfolio/${slug}`),
  create: (input: PortfolioItemInput) => api.post<PortfolioItem>("/portfolio", normalizeInput(input)),
  update: (id: string, input: PortfolioItemInput) => api.patch<PortfolioItem>(`/portfolio/${id}`, normalizeInput(input)),
  delete: (id: string) => api.delete<void>(`/portfolio/${id}`),
  uploadImages: (id: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("images", file));
    return api.post<PortfolioImage[]>(`/portfolio/${id}/images`, formData);
  },
  attachMediaImages: (id: string, mediaIds: string[]) =>
    api.post<PortfolioImage[]>(`/portfolio/${id}/images/from-media`, { mediaIds }),
  updateImage: (itemId: string, imageId: string, input: Partial<Pick<PortfolioImage, "alt" | "sortOrder" | "isPrimary">>) =>
    api.patch<PortfolioImage>(`/portfolio/${itemId}/images/${imageId}`, input),
};
